import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";

// Components
import {
  Container,
  Modal,
  Table,
  FormGroup,
  FormControl,
  FormText,
  Row,
  Col,
  Button,
  Spinner,
} from "react-bootstrap";
import { Formik, Form, Field } from "formik";

// Action
import { listUsers, saveUser } from "../actions/Users";
import { connect } from "react-redux";

import * as Yup from "yup";
import { IMAGES } from "../constants";
import RippleButton from "./Onboarding/RippleButton";

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("*First name is required"),
  lastName: Yup.string().required("*Last name is required"),
  email: Yup.string().email("*Invalid email").required("*Email Required"),
});

const ManageUsers = ({
  auth: { users, userGroup, loading },
  listUsers,
  saveUser,
}) => {
  useEffect(
    function loadUsers() {
      if (userGroup) {
        listUsers(userGroup);
      }
    },
    [userGroup, listUsers]
  );

  const initialFormData = {
    firstName: "",
    lastName: "",
    email: "",
    username: "",
  };

  const [isModalProcessing, setModalProcessing] = useState(false);
  const [changeUserError, setChangeUserError] = useState(false);
  const [show, setShow] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, updateFormData] = useState(initialFormData);

  const handleClose = () => setShow(false);

  const handleShow = (item) => {
    setShow(true);

    if (item) {
      setEditMode(true);
      updateFormData((prev) => ({
        ...prev,
        firstName: item.firstName,
        lastName: item.lastName,
        email: item.email,
      }));

      return;
    }

    setEditMode(false);
    updateFormData(initialFormData);
  };

  const handleSubmit = async (values) => {
    const { firstName, lastName, email } = values;
    setModalProcessing(true);

    try {
      const params = {
        firstName,
        lastName,
        email,
        username: email,
        groupName: userGroup,
      };
      await saveUser(params);
      handleClose();
    } catch (err) {
      /**
       * If error message is "Parse users error" mean save user success
       * but fail when parse the return data.
       *
       * Solution is refresh the list users by call query 'listUsers'
       */
      if (err.message === "Parse users error") {
        handleClose();
        listUsers(userGroup);
        return;
      }

      /**
       * Save user fail, show error on the modal
       */
      setChangeUserError(true);
    } finally {
      setModalProcessing(false);
    }
  };

  return (
    <Container fluid className="wrapper px-0">
      <Row className="mb-4 justify-content-between mx-0 title-setting-wrapper align-between">
        <div className="d-flex">
          <h3 className="title-setting title-menu-setting">Manage Users</h3>
          {loading && (
            <Spinner animation="border" className="ml-1" role="status" />
          )}
        </div>
        <Button
          variant="dark"
          className="download-template add-menu-button"
          onClick={() => handleShow(null)}
          size="sm"
        >
          Add User
          <img src={IMAGES.PLUS_ICON} alt="plus" height={17} width={17} />
        </Button>
      </Row>

      <Row className="mx-0 px-md-4 px-0">
        <Col>
          <Table hover className="mt-4 user-table">
            <tbody>
              {users &&
                users.map((item, i) => (
                  <Fragment key={i}>
                    <tr>
                      <td>
                        <div>
                          <p className="user-name text-truncate">
                            {item.firstName} {item.lastName}
                          </p>
                          <p className="user-email text-truncate">
                            {item.email}
                          </p>
                        </div>
                      </td>
                      <td align="right" className="pr-0">
                        <Button
                          key={i + "button"}
                          size="sm"
                          onClick={() => handleShow(item)}
                          className="menu-button bg-white"
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  </Fragment>
                ))}
            </tbody>
          </Table>
        </Col>
      </Row>

      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        dialogClassName="setting-modal user-modal"
      >
        <Formik
          enableReinitialize
          initialValues={{
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ errors, touched }) => (
            <Form>
              <Modal.Header>
                {/* Dynamically change text based upon Add/Edit user flow */}
                <Modal.Title className="title-setting">
                  {!editMode && <span>Add user</span>}
                  {editMode && <span>Edit user</span>}
                </Modal.Title>
                <img
                  src={IMAGES.CLOSE_ICON}
                  alt="close-icon"
                  width="44"
                  height="44"
                  onClick={handleClose}
                />
              </Modal.Header>
              <Modal.Body>
                <Field name="firstName">
                  {({ field }) => (
                    <FormGroup
                      controlId="firstName"
                      className="setting-form-group mb-2"
                    >
                      <FormControl
                        type="text"
                        value={field.value}
                        placeholder="Enter first name"
                        onChange={field.onChange}
                        isValid={touched.firstName && !errors.firstName}
                        isInvalid={touched.firstName && errors.firstName}
                        className="setting-input"
                        data-testid="first-name"
                      />
                      <FormControl.Feedback type="invalid">
                        {errors.firstName}
                      </FormControl.Feedback>
                    </FormGroup>
                  )}
                </Field>
                <Field name="lastName">
                  {({ field }) => (
                    <FormGroup
                      controlId="lastName"
                      className="setting-form-group mb-2"
                    >
                      <FormControl
                        type="text"
                        value={field.value}
                        placeholder="Enter last name"
                        onChange={field.onChange}
                        isValid={touched.lastName && !errors.lastName}
                        isInvalid={touched.lastName && errors.lastName}
                        className="setting-input"
                        data-testid="last-name"
                      />
                      <FormControl.Feedback type="invalid">
                        {errors.lastName}
                      </FormControl.Feedback>
                    </FormGroup>
                  )}
                </Field>
                <Field name="email">
                  {({ field }) => (
                    <FormGroup
                      controlId="email"
                      className="setting-form-group mb-2"
                    >
                      <FormControl
                        type="text"
                        value={field.value}
                        placeholder="Enter email"
                        onChange={field.onChange}
                        isValid={touched.email && !errors.email}
                        isInvalid={touched.email && errors.email}
                        className="setting-input"
                        data-testid="user-email"
                      />
                      <FormControl.Feedback type="invalid">
                        {errors.email}
                      </FormControl.Feedback>
                      <FormText
                        id="passwordHelpBlock"
                        className="user-form-text"
                      >
                        {!editMode && (
                          <span>
                            New Users will be sent a verification email
                            instruction to access their account
                          </span>
                        )}
                      </FormText>
                    </FormGroup>
                  )}
                </Field>
                {changeUserError && (
                  <div>
                    Sorry, we are not able to change the user at this time.
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer className="justify-content-center">
                <Button
                  disabled={isModalProcessing}
                  variant="outline-dark"
                  onClick={handleClose}
                  className="menu-link-button user-button"
                >
                  Close
                </Button>
                <RippleButton
                  disabled={isModalProcessing}
                  customStyles="setting-button"
                  type
                >
                  Save
                  {isModalProcessing && (
                    <Spinner
                      animation="border"
                      className="ml-1"
                      as="span"
                      size="sm"
                      role="isProcessing"
                      aria-hidden="true"
                    />
                  )}
                </RippleButton>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </Container>
  );
};

ManageUsers.propTypes = {
  auth: PropTypes.object.isRequired,
  listUsers: PropTypes.func.isRequired,
  saveUser: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { listUsers, saveUser })(ManageUsers);
